<script setup lang="ts">
import { computed, onMounted, onBeforeUnmount, ref, watch } from 'vue';
import InputSwitch from 'primevue/inputswitch';
import MultiSelect from 'primevue/multiselect';
import Menu from 'primevue/menu';
import ProgressSpinner from 'primevue/progressspinner';
import UserRemoveReferral from './components/UserRemoveReferral.vue';
import UserApproveReferral from './components/UserApproveReferral.vue';
import UserArchiveReferral from './components/UserArchiveReferral.vue';
import UserAutomatchReferral from './components/UserAutomatchReferral.vue';
import UserAssignToServiceProvider from './components/UserAssignToServiceProvider.vue';
import { PERMISSIONS, TENANT_SETTINGS } from '@/enum';
import { can, useDateTime, useRouting, useRequestCancel, useTenant } from '@/composables';
import { useStudentReferralStore, useCustomerSuccessPartnerStore } from '@/store';
import { UserReferralListFilterInterface, ReferralPayloadInterface, StudentClinicianInterface } from '@/types';
import { DataTablePageEvent, DataTableSortEvent } from 'primevue/datatable';

const $routing = useRouting();
const $tenant = useTenant();
const referralStore = useStudentReferralStore();
// Only show organizations for students on the current page
const filteredOrganizations = computed(() => {
  const studentOrgIds = new Set(referralStore.students.data.map((student) => student.organization_id));
  return (referralStore.organizations || []).filter((org) => studentOrgIds.has(org.id));
});
const requestCancel = useRequestCancel();
const referralsSelected = ref<number[]>([]);
const selectedStudentNames = ref<Record<number, string>>({});
const allColumns = ref<boolean>(false);
const deleteId = ref(1);
const openApprove = ref<boolean>(false);
const openRemove = ref<boolean>(false);
const openAssign = ref<boolean>(false);
const openArchive = ref<boolean>(false);
const openAutomatch = ref<boolean>(false);
const payload = ref<ReferralPayloadInterface[]>([]);
const currentReferralApprovingId = ref<number>();
const referralTimeout = 2000;
const noSPSelected = ref<number[]>([]);
const organizationId = ref<string | null>(null);
const filterKeys = ['organization_id', 'referral_type_id', 'search'];
const filters = ref<UserReferralListFilterInterface>({} as UserReferralListFilterInterface);
const globalServiceProvider = ref<StudentClinicianInterface | null>(null);
const customerSuccessPartnerStore = useCustomerSuccessPartnerStore();
const suppressOrgChange = ref(false);
const defaultFilters = {
  search: '',
  order: '',
  order_by: '',
  page: 1,
  per_page: 15,
  referral_type_id: '',
  organization_id: '',
  grade: '',
  discipline: '',
};

onMounted(async () => init());

onBeforeUnmount(() => {
  customerSuccessPartnerStore.selectedUsersSuccessPartners = [];
  requestCancel.requestSaverAbortAll();
});

const hasFilter = computed<boolean>(() => {
  let isFilter = false;
  Object.keys($routing.route.query).forEach((key) => {
    if (filterKeys.includes(key)) isFilter = true;
  });
  return isFilter;
});

const isUserList = computed<boolean>(
  () => $routing.route.name === 'user.list' || $routing.route.name === 'user.referrals',
);

const init = async () => {
  if (isUserList.value && can([PERMISSIONS.REFERRAL_ASSIGN])) {
    defineFilters();
    await referralStore.getStudents(filters.value);
    await referralStore.loadOptions();
    await referralStore.getOrganizations();
  }
};

$routing.routeChangeWatcher(async () => {
  defineFilters();
  await referralStore.getStudents(filters.value);
  await defineSelectAllReferrals();
});

// Check whether or not the current set of students displayed match
// the total previously selected in order to mark the select all
// checkbox as checked.
const defineSelectAllReferrals = async () => {
  const currentStudentIds = referralStore.students.data.map((item) => item.id);
  const allSelected: number[] = [];

  referralStore.students.data.forEach((item) => {
    const index = referralsSelected.value.findIndex((id) => id === item.id);
    if (index !== -1) allSelected.push(index);
  });

  selectAllReferrals.value = allSelected.length === currentStudentIds.length;
};

const defineFilters = async () => {
  const query = $routing.route.query;

  let orgIdValue = '';

  if (query.organization_id) {
    if (typeof query.organization_id === 'string') {
      orgIdValue = query.organization_id;
    } else if (Array.isArray(query.organization_id)) {
      orgIdValue = query.organization_id.join(',');
    }
  }

  filters.value = {
    search: query.search ? (query.search as string) : '',
    page: query.page ? parseInt(query.page as string) : '',
    per_page: query.per_page ? parseInt(query.per_page as string) : defaultFilters.per_page,
    referral_type_id: query.referral_type_id ? parseInt(query.referral_type_id as string) : '',
    organization_id: orgIdValue,
    order: query.order ? (query.order as string) : '',
    order_by: query.order_by ? (query.order_by as string) : '',
  };
};

const onClearFilters = () => {
  suppressOrgChange.value = true;
  filters.value = { ...(defaultFilters as UserReferralListFilterInterface) };
  organizationId.value = null;
  customerSuccessPartnerStore.selectedUsersSuccessPartners = [];
  $routing.removeAllQueryParam();
  setTimeout(() => {
    suppressOrgChange.value = false;
  }, 0);
};

const onReferralTypeChange = () => $routing.addQueryParam('referral_type_id', filters.value.referral_type_id as string);

const onOrganizationChange = () => {
  let selected = organizationId.value ? [organizationId.value] : [];

  const cspOrgIds = customerSuccessPartnerStore.selectedUsersSuccessPartners
    ? customerSuccessPartnerStore.selectedUsersSuccessPartners.flatMap((csp) => csp.organizations || [])
    : [];

  const merged = Array.from(new Set([...selected, ...cspOrgIds].filter(Boolean)));

  $routing.addQueryParam('organization_id', merged.join(','));

  filters.value.organization_id = merged;
  selectAllReferrals.value = false;
  referralsSelected.value = [];
};

const onApproveSave = () => {
  if (payload.value.length === 0) {
    referralStore.doToast(`Please select at least one referral to approve.`, '', true);
    return;
  }
  referralStore.approving = true;
  noSPSelected.value = [];
  payload.value.forEach(async (referral, index) => {
    if (referral.service_provider_id) {
      setTimeout(async () => {
        currentReferralApprovingId.value = referral.id;

        await referralStore.approveReferrals({
          id: referral.id,
          service_provider_id: referral.service_provider_id,
        });

        if (index === payload.value.length - 1) {
          resetReferralArray();
          setTimeout(() => {
            referralStore.approving = false;
            init();
          }, referralTimeout);
        }
      }, index * referralTimeout);
    } else {
      noSPSelected.value.push(referral.id);
      if (index === payload.value.length - 1) {
        resetReferralArray();
        setTimeout(() => {
          referralStore.approving = false;
          init();
        }, referralTimeout);
      }
    }
  });

  currentReferralApprovingId.value = undefined;
};

const onArchiveSave = () => {
  if (payload.value.length === 0) {
    referralStore.doToast(`Please select at least one student to archive.`, '', true);
    return;
  }

  referralStore.approving = true;

  payload.value.forEach(async (referral, index) => {
    setTimeout(async () => {
      currentReferralApprovingId.value = referral.id;
      await referralStore.archiveReferrals(referral.id, referral.student_id);

      if (index === payload.value.length - 1) {
        resetReferralArray();
        setTimeout(() => {
          referralStore.approving = false;
          init();
        }, referralTimeout);
      }
    }, index * referralTimeout);
  });

  currentReferralApprovingId.value = undefined;
};

const onAutomatchSave = () => {
  if (payload.value.length === 0) {
    referralStore.doToast(`Please select at least one referral to automatch.`, '', true);
    return;
  }

  referralStore.approving = true;
  noSPSelected.value = [];

  payload.value.forEach(async (referral, index) => {
    setTimeout(async () => {
      currentReferralApprovingId.value = referral.id;
      await referralStore.automatchReferrals(referral.id);

      if (index === payload.value.length - 1) {
        resetReferralArray();
        setTimeout(() => {
          referralStore.approving = false;
          init();

          // Keep track of the referal id's that have not been automatched.
          if (referralStore.unApprovedStudentIds?.length) {
            referralStore.unApprovedStudentIds.forEach((item) => {
              referralsSelected.value.push(item);
            });
          }
        }, referralTimeout);
      }
    }, index * referralTimeout);
  });

  currentReferralApprovingId.value = undefined;
};

const resetReferralArray = () => {
  setTimeout(() => {
    payload.value = [];
    referralsSelected.value = [...noSPSelected.value];
    currentReferralApprovingId.value = undefined;
  }, referralTimeout);
};

const onSearch = (search: string) => {
  const hasSearchTerm = search.length >= 3;

  if (hasSearchTerm) {
    $routing.addQueryParam('search', search);
    filters.value.search = search;
  } else {
    $routing.removeQueryParam('search');
  }
};

const onPage = (event: DataTablePageEvent) => {
  filters.value.per_page = event.rows;
  $routing.addQueryParams({ per_page: event.rows, page: event.page + 1 });

  referralStore.students.data.forEach((student) => {
    if (referralsSelected.value.includes(student.id)) {
      student.selected = true;
      selectedStudentNames.value[student.id] = student.student_name;
    }
  });
  formatSelectedStudentNamesAndSetPayload();
};

const onSort = (event: DataTableSortEvent) => {
  const order = event.sortOrder === 1 ? 'asc' : 'desc';
  const field = event.sortField;
  filters.value.order = order;
  filters.value.order_by = field as string;
  $routing.addQueryParams({ order, order_by: field });
};

const formattedStudentNames = ref('');

const openModal = (modal: 'approve' | 'remove' | 'assign' | 'archive' | 'automatch') => {
  if (modal === 'approve') {
    formatSelectedStudentNamesAndSetPayload();
    openApprove.value = true;
  }

  if (modal === 'automatch') {
    formatSelectedStudentNamesAndSetPayload();
    openAutomatch.value = true;
  }

  if (modal === 'archive') {
    formatSelectedStudentNamesAndSetPayload();
    openArchive.value = true;
  }

  if (modal === 'remove') openRemove.value = true;
  if (modal === 'assign') openAssign.value = true;
};

const closeModal = (modal: 'approve' | 'remove' | 'assign' | 'archive' | 'automatch') => {
  if (modal === 'approve') openApprove.value = false;
  if (modal === 'remove') openRemove.value = false;
  if (modal === 'assign') openAssign.value = false;
  if (modal === 'archive') openArchive.value = false;
  if (modal === 'automatch') openAutomatch.value = false;
};

const assignStudentToServiceProvider = (serviceProvider: StudentClinicianInterface) => {
  globalServiceProvider.value = serviceProvider;
  const ids = referralsSelected.value;

  if (ids.length) {
    ids.forEach((id) => {
      // Update current page students for immediate UI update
      referralStore.students.data.forEach((student) => {
        if (student.id === id) {
          student.service_provider_id = serviceProvider.id;
          student.service_provider_name = serviceProvider.display_name;
        }
      });

      // Update payload for persistence
      let found = payload.value.find((item) => item.id === id);

      if (found) {
        found.service_provider_id = serviceProvider.id;
      } else {
        // Optionally add to payload if not present
        const student = referralStore.students.data.find((s) => s.id === id);

        if (student) {
          payload.value.push({
            id: student.id,
            service_provider_id: serviceProvider.id,
            index: student.index,
            student_name: student.student_name,
            page: filters.value.page,
          });
        }
      }
    });
  }
};

const formatSelectedStudentNamesAndSetPayload = () => {
  const ids = referralsSelected.value;
  if (ids.length) {
    const nameArray: string[] = [];

    ids.forEach((id) => {
      const name = selectedStudentNames.value[id]; // Use the mapping for names
      if (name) {
        nameArray.push(name);
      }

      const found = referralStore.students.data.find((student) => student.id === id);
      const studentIndex = referralStore.students.data.findIndex((student) => student.id === id);

      const inPayload = payload.value.find((item) => item.id === id);

      if (!filters.value.page) {
        filters.value.page = 1;
      }

      if (!inPayload && found) {
        payload.value.push({
          id: found.id,
          service_provider_id: found.service_provider_id,
          index: studentIndex,
          student_id: found.student_id,
          student_name: found.student_name,
          page: filters.value.page,
        });
      }
    });

    payload.value = Object.values(payload.value).sort((a, b) => (a.index > b.index ? 1 : -1));

    formattedStudentNames.value = nameArray.join(', ');
  }
};

const referredSuccessfully = (id: number) => referralStore.approvedStudentIds?.includes(id);

const handleCheckboxChange = async (checked: boolean, id: number, name: string) => {
  if (checked) {
    selectedStudentNames.value[id] = name;
  } else {
    delete selectedStudentNames.value[id];
  }

  if (referralStore.unApprovedStudentIds?.length) {
    const index = referralStore.unApprovedStudentIds.findIndex((item) => item === id);

    if (index > -1) {
      referralStore.unApprovedStudentIds.splice(index, 1);
    }
  }

  await defineSelectAllReferrals();
};

const updateReferrals = (updatedReferrals: ReferralPayloadInterface[]) => {
  payload.value = updatedReferrals;
  referralsSelected.value = updatedReferrals.map((referral) => referral.id);
};

const handleDropdownChange = (serviceProviderId: number, student: ReferralPayloadInterface) => {
  let found = payload.value.find((item) => item.id === student.id);
  if (found) {
    found.service_provider_id = serviceProviderId.value;
  } else {
    payload.value.push({
      id: student.id,
      service_provider_id: serviceProviderId.value,
      index: student.index,
      student_name: student.student_name,
      page: filters.value.page,
    });
  }
};

watch(
  () => referralStore.students?.data || [],
  (students) => {
    if (students?.length) {
      students.forEach((student) => {
        const savedReferral = payload.value.find((item) => item.id === student.id);
        if (savedReferral) {
          student.service_provider_id = savedReferral.service_provider_id;
        }
      });
    }
  },
  { immediate: true },
);

watch(
  () => customerSuccessPartnerStore.selectedUsersSuccessPartners,
  () => {
    if (!suppressOrgChange.value) {
      onOrganizationChange();
    }
  },
  { deep: true },
);

const actions = ref();
const selectAllReferrals = ref();

const actionItems = (() => {
  return [
    {
      label: 'Approve',
      icon: {
        name: 'add',
        styles: 'inline-block text-lg text-orange-500',
      },
      command: () => openModal('approve'),
    },
    {
      label: 'Archive',
      icon: {
        name: 'vpn_key',
        styles: 'inline-block text-lg text-orange-500',
      },
      command: () => openModal('archive'),
    },
    {
      label: 'Assign to Service Provider',
      icon: {
        name: 'assignment',
        styles: 'inline-block text-lg text-orange-500',
      },
      command: () => openModal('assign'),
    },
    {
      label: 'Auto Match',
      icon: {
        name: 'assignment',
        styles: 'inline-block text-lg text-orange-500',
      },
      command: () => openModal('automatch'),
      visible:
        $tenant.tenantSettingEnabled(TENANT_SETTINGS.USE_SP_SCHOOL_AUTOMATCHING) &&
        $tenant.tenantSettingEnabled(TENANT_SETTINGS.USE_AUTOMATCHING_FOR_UNASSIGNED) &&
        $tenant.tenantSettingEnabled(TENANT_SETTINGS.USE_AUTOMATCHING_FOR_REFERRALS),
    },
  ];
})();

const onActionsToggle = (event: Event) => actions.value.toggle(event);

const onSelectAllReferrals = () => {
  if (selectAllReferrals.value) {
    referralStore.students.data.forEach((item) => {
      const exists = referralsSelected.value.find((id) => id === item.id);

      if (!exists) {
        referralsSelected.value.push(item.id);
        selectedStudentNames.value[item.id] = item.student_name;
      }
    });
  } else {
    referralStore.students.data.forEach((item) => {
      const index = referralsSelected.value.findIndex((id) => id === item.id);

      if (index !== -1) {
        delete referralsSelected.value[index];
        delete selectedStudentNames.value[item.id];
      }
    });
  }
};
</script>

<template>
  <UserRemoveReferral :id="deleteId" :open="openRemove" @update="init" @cancel="closeModal('remove')" />
  <UserApproveReferral
    :referrals="payload"
    :open="openApprove"
    @save="onApproveSave"
    @cancel="closeModal('approve')"
    @update-referrals="updateReferrals"
  />
  <UserArchiveReferral
    :referrals="payload"
    :open="openArchive"
    @save="onArchiveSave"
    @cancel="closeModal('archive')"
    @update-referrals="updateReferrals"
  />
  <UserAutomatchReferral
    :referrals="payload"
    :open="openAutomatch"
    @save="onAutomatchSave"
    @cancel="closeModal('automatch')"
    @update-referrals="updateReferrals"
  />
  <UserAssignToServiceProvider
    v-if="openAssign && can([PERMISSIONS.REFERRAL_ASSIGN])"
    :ids="referralsSelected"
    @save="assignStudentToServiceProvider"
    @cancel="closeModal('assign')"
  />
  <div class="page-content" style="position: relative">
    <div v-if="referralStore.studentsLoading" class="table-overlay">
      <ProgressSpinner style="width: 50px; height: 50px" />
    </div>
    <Card v-if="can([PERMISSIONS.REFERRAL_ASSIGN])">
      <template #content>
        <div class="flex flex-wrap gap-3 m-3 align-items-center">
          <div class="flex align-items-center gap-3 flex-1">
            <IconField icon-position="left">
              <InputIcon>
                <Icon name="search" class="text-lg" />
              </InputIcon>
              <InputText
                v-model="filters.search"
                v-debounce:600ms="onSearch"
                :disabled="referralStore.approving || referralStore.studentsLoading"
                class="w-full min-w-40"
                style="min-width: 200px"
                placeholder="Search by Student Name"
              />
            </IconField>
            <Skeleton v-if="referralStore.loading" height="36px" width="160px" border-radius="4" />
            <Dropdown
              v-else
              id="organizations"
              v-model="organizationId"
              filter
              :disabled="referralStore.approving || referralStore.studentsLoading"
              type="secondary"
              :options="filteredOrganizations"
              option-label="name"
              option-value="id"
              placeholder="Filter by Organization"
              style="width: 200px; max-width: 200px"
              @change="onOrganizationChange"
            />
            <Skeleton v-if="referralStore.loading" height="36px" width="160px" border-radius="4" />
            <Dropdown
              v-else
              id="referral_types"
              v-model="filters.referral_type_id"
              :disabled="referralStore.approving || referralStore.studentsLoading"
              type="secondary"
              :options="referralStore.referralTypes"
              option-label="title"
              option-value="id"
              placeholder="Filter by Referral Type"
              style="width: 200px; max-width: 200px"
              @change="onReferralTypeChange"
            />
            <MultiSelect
              v-if="$tenant.tenantSettingEnabled(TENANT_SETTINGS.UTILIZATION_TRACKING)"
              v-model="customerSuccessPartnerStore.selectedUsersSuccessPartners"
              :options="customerSuccessPartnerStore.customerSuccessPartners"
              option-label="display_name"
              :max-selected-labels="2"
              :loading="customerSuccessPartnerStore.loading"
              :disabled="referralStore.approving || referralStore.studentsLoading"
              placeholder="All CSPs"
            />
            <div class="flex align-items-center">
              <div class="mr-2 pt-1" style="float: left">
                <InputSwitch
                  v-model="allColumns"
                  :disabled="referralStore.approving || referralStore.studentsLoading"
                />
              </div>
              <div class="w-4rem text-gray-600 font-normal line-height-1 align-items-center" style="float: left">
                <label class="text-xs mt-1">Show All Columns</label>
              </div>
            </div>
            <div>
              <AppButton
                v-if="hasFilter"
                type="secondary"
                text="Clear Filters"
                icon-left="clear"
                :disabled="referralStore.approving || referralStore.studentsLoading"
                @click="onClearFilters"
              />
            </div>
          </div>
          <div>
            <AppButton
              type="secondary"
              text="Actions"
              icon-left="more_horiz"
              aria-haspopup="true"
              aria-controls="referral_actions"
              :disabled="referralsSelected.length === 0 || referralStore.approving || referralStore.studentsLoading"
              @click="onActionsToggle"
            />
            <Menu id="referral_actions" ref="actions" class="mt-2 ml-2 p-2 w-max" :model="actionItems" :popup="true" />
          </div>
        </div>
        <DataTable
          v-model:filters="filters"
          v-model:selection="referralsSelected"
          :resizable-columns="true"
          column-resize-mode="fit"
          filter-display="menu"
          :value="referralStore.students.data"
          :paginator="true"
          class="p-datatable-customers"
          :class="{ 'table-loading': referralStore.studentsLoading }"
          :rows="filters.per_page"
          :total-records="referralStore.students.total"
          data-key="id"
          loading-icon="logo-spinner"
          :loading="referralStore.studentsLoading"
          :rows-per-page-options="[15, 25, 30]"
          paginator-template="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
          current-page-report-template="Showing {first} to {last} of {totalRecords}"
          responsive-layout="scroll"
          :lazy="true"
          @page="onPage"
          @sort="onSort"
        >
          <template #empty>No referrals found</template>
          <template v-if="!referralStore.studentsLoading && referralStore.students.total">
            <Column class="px-2" header-class="w-0">
              <template #header>
                <Checkbox v-model="selectAllReferrals" binary :value="true" @change="onSelectAllReferrals" />
              </template>
              <template #body="{ data }">
                <Icon
                  v-if="referralStore.approving && currentReferralApprovingId === data.id"
                  name="cached"
                  class="text-primary spin-cached"
                />
                <Icon v-else-if="referredSuccessfully(data.id)" name="check_circle" class="text-primary" />
                <Checkbox
                  v-else
                  v-model="referralsSelected"
                  :disabled="referralStore.approving || referralStore.studentsLoading"
                  :value="data.id"
                  @change="(checked) => handleCheckboxChange(checked, data.id, data.student_name)"
                />
              </template>
            </Column>
            <Column field="student_id" header="SSID" sortable>
              <template #body="{ data }">
                <div
                  class="flex h-full justify-content-center text-center"
                  v-text="data.student_external_id ?? 'N/A'"
                />
              </template>
            </Column>
            <Column field="student" header="Student" sortable>
              <template #body="{ data }">
                <div class="justify-content-center">
                  <RouterLink
                    :to="`/user/${data.student_id}`"
                    target="_blank"
                    class="font-semibold"
                    v-text="data.student_name"
                  />
                </div>
              </template>
            </Column>
            <Column field="service_provider" header="Service Provider" :sortable="true">
              <template #body="{ data }">
                <Dropdown
                  v-model="data.service_provider_id"
                  type="secondary"
                  :options="referralStore.getStudentOrganizationServiceProviders(data.organization_id)"
                  option-label="display_name"
                  option-value="id"
                  placeholder="Service Provider"
                  :disabled="referralStore.approving || referralStore.studentsLoading"
                  @change="(value) => handleDropdownChange(value, data)"
                />
              </template>
            </Column>
            <Column field="organization" header="Organization" sortable>
              <template #body="{ data }">
                <RouterLink
                  :to="`/organizations/${data.organization_id}`"
                  target="_blank"
                  class="font-semibold"
                  v-text="data.organization_name"
                />
              </template>
            </Column>
            <Column field="building_campus" header="Building/Campus" sortable>
              <template #body="{ data }">
                <div class="flex h-full justify-content-center">
                  <span>{{ data.student_building_campus ?? 'N/A' }}</span>
                </div>
              </template>
            </Column>
            <Column field="grade" header="Grade" sortable>
              <template #body="{ data }">
                <div class="flex h-full justify-content-center">
                  <span>{{ data.grade }}</span>
                </div>
              </template>
            </Column>
            <Column field="discipline" header="Discipline" sortable>
              <template #body="{ data }">
                <div class="flex h-full justify-content-center">
                  <span>
                    <template v-if="Array.isArray(data.treatment_service_discipline)">
                      <template v-if="data.treatment_service_discipline.length > 1">
                        {{ data.treatment_service_discipline.map((d) => d.code || d.name || d).join(', ') }}
                      </template>
                      <template v-else-if="data.treatment_service_discipline.length === 1">
                        {{
                          data.treatment_service_discipline[0].name ||
                          data.treatment_service_discipline[0].code ||
                          data.treatment_service_discipline[0]
                        }}
                      </template>
                      <template v-else>N/A</template>
                    </template>
                    <template
                      v-else-if="
                        data.treatment_service_discipline && typeof data.treatment_service_discipline === 'object'
                      "
                    >
                      {{ data.treatment_service_discipline.name || data.treatment_service_discipline.code || 'N/A' }}
                    </template>
                    <template v-else>
                      {{ data.treatment_service_discipline ?? 'N/A' }}
                    </template>
                  </span>
                </div>
              </template>
            </Column>
            <Column field="referred_at" header="Date of Referral" sortable>
              <template #body="{ data }">
                <div
                  class="flex h-full justify-content-center"
                  v-text="useDateTime().formatIntlDate(data.referred_at)"
                />
              </template>
            </Column>
            <Column field="reason" header="Reason" sortable>
              <template #body="{ data }">
                <span>{{ data.reason ?? 'N/A' }}</span>
              </template>
            </Column>
            <Column field="days_since_service" header="Days Since Last Service" sortable>
              <template #body="{ data }">
                <div class="flex h-full justify-content-center">
                  <span>{{ data.days_since_last_service }}</span>
                </div>
              </template>
            </Column>
            <Column field="referral_type" header="Referral Type" sortable>
              <template #body="{ data }">
                <span>{{ data.referral_type_title ?? 'N/A' }}</span>
              </template>
            </Column>
            <Column field="referral_reason" header="Referral Reason" sortable>
              <template #body="{ data }">
                <span>{{ data.referral_reason_title ?? 'N/A' }}</span>
              </template>
            </Column>
            <Column field="referral_urgency" header="Urgency" sortable>
              <template #body="{ data }">
                <span>{{ data.referral_urgency_title ?? 'N/A' }}</span>
              </template>
            </Column>
            <template v-if="allColumns">
              <Column field="treatment_type" header="Plan Type" sortable>
                <template #body="{ data }">
                  <span>{{ data.treatment_type_name ?? 'N/A' }}</span>
                </template>
              </Column>
              <Column field="treatment_category" header="Service Category" sortable>
                <template #body="{ data }">
                  <span>{{ data.treatment_category_name ?? 'N/A' }}</span>
                </template>
              </Column>
              <Column field="treatment_service" header="Service" sortable>
                <template #body="{ data }">
                  <span>{{ data.treatment_service_discipline ?? 'N/A' }}</span>
                </template>
              </Column>
            </template>
          </template>
        </DataTable>
      </template>
    </Card>
  </div>
</template>

<style lang="scss" scoped>
:deep(.p-card-body) {
  padding: 0px;

  .p-datatable {
    margin: 0 !important;
    .p-datatable-tbody {
      tr {
        td {
          .p-dropdown {
            height: 1.8rem;
          }
        }
      }
    }
  }

  .p-paginator-current {
    flex: 1;
    justify-content: flex-end;
  }
}

.table-overlay {
  position: absolute;
  top: 60px;
  left: 0;
  width: 100%;
  height: calc(100% - 60px);
  background: rgba(255, 255, 255, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
}

.text-red-600 {
  color: $red;
}

.table-loading {
  cursor: progress !important;
}
</style>
